import { get, post, put, del } from "@/shared/utils/fetchApi";
import type {
  ApiToken,
  ApiTokenAuditLog,
  CreateApiTokenDTO,
  GeneratedApiToken,
  UpdateApiTokenDTO,
} from "../types/apiToken.types";

/**
 * `/api-tokens` responde DTOs snake_case flat, sem envelope `{success,data}`, o
 * que casa 1:1 com os tipos locais. O corpo da requisição vai em camelCase
 * porque é o que os validators da api esperam — daí o mapeamento explícito nos
 * métodos de escrita.
 */
export class ApiTokenService {
  private static readonly BASE_URL = "/api-tokens";

  static async create(data: CreateApiTokenDTO): Promise<GeneratedApiToken> {
    return post<GeneratedApiToken>(this.BASE_URL, {
      productId: data.product_id,
      tokenName: data.token_name,
      expiresAt: data.expires_at ?? null,
    });
  }

  static async listByWorkspace(workspaceId: string): Promise<ApiToken[]> {
    return get<ApiToken[]>(`${this.BASE_URL}/product/${workspaceId}`);
  }

  static async getById(id: string): Promise<ApiToken> {
    return get<ApiToken>(`${this.BASE_URL}/${id}`);
  }

  static async update(id: string, data: UpdateApiTokenDTO): Promise<ApiToken> {
    const body: Record<string, unknown> = {};
    if (data.token_name !== undefined) body.tokenName = data.token_name;
    if (data.is_active !== undefined) body.isActive = data.is_active;
    if (data.expires_at !== undefined) body.expiresAt = data.expires_at;
    return put<ApiToken>(`${this.BASE_URL}/${id}`, body);
  }

  static async revoke(id: string): Promise<ApiToken> {
    return post<ApiToken>(`${this.BASE_URL}/${id}/revoke`);
  }

  static async renew(id: string, expiresAt?: string | null): Promise<ApiToken> {
    return post<ApiToken>(`${this.BASE_URL}/${id}/renew`, {
      expiresAt: expiresAt ?? null,
    });
  }

  static async regenerate(id: string): Promise<GeneratedApiToken> {
    return post<GeneratedApiToken>(`${this.BASE_URL}/${id}/regenerate`);
  }

  static async delete(id: string): Promise<void> {
    await del<{ success: boolean }>(`${this.BASE_URL}/${id}`);
    return;
  }

  static async getAuditLogs(
    id: string,
    limit = 100,
  ): Promise<ApiTokenAuditLog[]> {
    return get<ApiTokenAuditLog[]>(
      `${this.BASE_URL}/${id}/audit-logs?limit=${limit}`,
    );
  }
}
